import { action, computed } from "@ember/object"
import { inject as service } from "@ember/service"
import Component from "@glimmer/component"
import { tracked } from "@glimmer/tracking"
import type { Course } from "@shared/models/course"
import type { Planbook } from "@shared/models/planbook"
import type { RotationCalendar } from "@shared/models/rotation-calendar"
import { handleError } from "cc-frontend/error-handling"
import type EmberStore from "cc-frontend/services/ember-store"
import type RotationEditor from "cc-frontend/services/rotation-editor"
import * as R from "remeda" // tree-shaking supported!

interface Args {
  planbook: Planbook
  rotationCalendar: RotationCalendar
}

export default class ChangeStartOrEnd extends Component<Args> {
  @tracked startMenuIsOpen = false
  @tracked endMenuIsOpen = false
  @tracked dateCenter = false

  @service declare rotationEditor: RotationEditor
  @service("ember-store") declare store: EmberStore

  @computed("args.rotationCalendar.attributes.semesters.@each.startDate")
  get startDate() {
    return R.pipe(
      this.args.rotationCalendar.attributes.semesters,
      R.map((semester) => semester.startDate),
      R.sort((a, b) => a.localeCompare(b)),
      R.first()
    )
  }

  @computed("args.rotationCalendar.attributes.semesters.@each.endDate")
  get endDate() {
    return R.pipe(
      this.args.rotationCalendar.attributes.semesters,
      R.map((semester) => semester.endDate),
      R.sort((a, b) => b.localeCompare(a)),
      R.first()
    )
  }

  @action
  async setStartDate({ date }) {
    let dateString = dateFns.format(date, "YYYY-MM-DD")
    if (this.endDate && dateString > this.endDate) {
      if (window && window.swal) {
        window
          .swal({
            title: "The start date cannot be after than end date.",
            type: "warning",
            showCancelButton: false,
          })
          .catch((error) => {
            if (error instanceof Error) handleError(error)
          })
      }
    } else {
      const courses = await Promise.all(
        this.args.planbook.relationships.courses.data
          .filter((relation) => !!relation.id)
          .map((relation) => this.store.find("course", relation.id) as Promise<Course>)
      )
      this.rotationEditor.changeSchoolYearDates({
        courses,
        planbook: this.args.planbook,
        rotationCalendar: this.args.rotationCalendar,
        dateString: dateString,
        startOrEnd: "START",
      })
    }
    this.startMenuIsOpen = false
  }

  @action
  async setEndDate({ date }) {
    let dateString = dateFns.format(date, "YYYY-MM-DD")
    if (dateFns.differenceInDays(dateString, this.startDate) > 365) {
      let daysAfterStartDate = dateFns.differenceInDays(dateString, this.startDate)
      if (window && window.swal) {
        window
          .swal({
            title: "The school year cannot be longer than 365 days.",
            text: `This date is ${daysAfterStartDate} days after the start of your school year.`,
            type: "warning",
            showCancelButton: false,
          })
          .catch((error) => {
            if (error instanceof Error) handleError(error)
          })
      }
    } else {
      const courses = await Promise.all(
        this.args.planbook.relationships.courses.data
          .filter((relation) => !!relation.id)
          .map((relation) => this.store.find("course", relation.id) as Promise<Course>)
      )
      this.rotationEditor.changeSchoolYearDates({
        courses,
        planbook: this.args.planbook,
        rotationCalendar: this.args.rotationCalendar,
        dateString: dateString,
        startOrEnd: "END",
      })
    }
    this.endMenuIsOpen = false
  }
  @action
  setDateCenter({ date }) {
    this.dateCenter = dateFns.format(date, "YYYY-MM-DD")
  }
}

declare module "@glint/environment-ember-loose/registry" {
  export default interface Registry {
    "Planner::Menus::PlanbookMenu::ChangeStartOrEnd::Body": typeof ChangeStartOrEnd
  }
}
